// src/components/attestations/DocumentViewer.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getPresignedDownloadUrl } from '../../store/actions/attestationActions';
import LoadingSpinner from '../common/LoadingSpinner';

const DocumentViewer = ({ document, attestationId, onClose }) => {
  const dispatch = useDispatch();
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isImage = document?.fileType?.startsWith('image/');
  const isPDF = document?.fileType?.includes('pdf') || document?.name?.endsWith('.pdf');
  const isVideo = document?.fileType?.startsWith('video/');
  const isAudio = document?.fileType?.startsWith('audio/');
  const isText = document?.fileType?.startsWith('text/') || document?.name?.endsWith('.txt');

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
    },
    container: {
      width: '90%',
      height: '90%',
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    header: {
      padding: '1rem 1.5rem',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#f9fafb',
    },
    title: {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#111827',
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#6b7280',
      padding: '0.25rem 0.5rem',
      borderRadius: '0.375rem',
      transition: 'all 0.2s',
    },
    closeButtonHover: {
      backgroundColor: '#e5e7eb',
      color: '#111827',
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: '2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f3f4f6',
    },
    imageViewer: {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    pdfViewer: {
      width: '100%',
      height: '100%',
      border: 'none',
    },
    videoViewer: {
      maxWidth: '100%',
      maxHeight: '100%',
    },
    audioViewer: {
      width: '100%',
      maxWidth: '600px',
    },
    textViewer: {
      width: '100%',
      height: '100%',
      padding: '2rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      fontFamily: 'monospace',
      whiteSpace: 'pre-wrap',
      overflow: 'auto',
      border: '1px solid #e5e7eb',
    },
    unsupported: {
      textAlign: 'center',
      color: '#6b7280',
    },
    downloadButton: {
      marginTop: '1rem',
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '1rem',
    },
    errorContainer: {
      textAlign: 'center',
      color: '#dc2626',
    },
    metadata: {
      marginTop: '1rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      color: '#6b7280',
    },
  };

  useEffect(() => {
    const fetchDownloadUrl = async () => {
      if (!document || !attestationId) {
        setLoading(false);
        return;
      }

      try {
        // If it's a public URL from S3, use it directly
        if (document.storagePath && document.storagePath.startsWith('http')) {
          setFileUrl(document.storagePath);
        } else if (document.storagePath) {
          // Otherwise get a presigned URL from the backend
          const response = await dispatch(getPresignedDownloadUrl(attestationId, document.id));
          setFileUrl(response.downloadUrl);
        }
      } catch (err) {
        console.error('Failed to get download URL:', err);
        setError('Impossible de charger le document');
      } finally {
        setLoading(false);
      }
    };

    fetchDownloadUrl();
  }, [document, attestationId, dispatch]);

  const handleDownload = () => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div style={styles.loadingContainer}>
          <LoadingSpinner />
          <div>Chargement du document...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div style={styles.errorContainer}>
          <svg style={{ width: '3rem', height: '3rem', marginBottom: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>Impossible de charger le document</div>
          <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>{error}</div>
          <button onClick={handleDownload} style={styles.downloadButton}>
            Télécharger directement
          </button>
        </div>
      );
    }

    if (!fileUrl) {
      return (
        <div style={styles.unsupported}>
          <svg style={{ width: '4rem', height: '4rem', marginBottom: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <div style={{ fontSize: '1rem', fontWeight: 500, marginBottom: '0.5rem' }}>
            URL de téléchargement non disponible
          </div>
          <button onClick={handleDownload} style={styles.downloadButton}>
            Réessayer
          </button>
        </div>
      );
    }

    if (isImage) {
      return (
        <img
          src={fileUrl}
          alt={document.originalName || document.name}
          style={styles.imageViewer}
          onError={() => setError("Impossible de charger l'image")}
        />
      );
    }

    if (isPDF) {
      return (
        <iframe
          src={`${fileUrl}#view=FitH`}
          title={document.name}
          style={styles.pdfViewer}
          onError={() => setError('Impossible de charger le PDF')}
        />
      );
    }

    if (isVideo) {
      return (
        <video
          controls
          autoPlay
          style={styles.videoViewer}
          onError={() => setError('Impossible de charger la vidéo')}
        >
          <source src={fileUrl} type={document.fileType} />
          Votre navigateur ne supporte pas la lecture de vidéos.
        </video>
      );
    }

    if (isAudio) {
      return (
        <audio
          controls
          autoPlay
          style={styles.audioViewer}
          onError={() => setError('Impossible de charger le fichier audio')}
        >
          <source src={fileUrl} type={document.fileType} />
          Votre navigateur ne supporte pas la lecture audio.
        </audio>
      );
    }

    if (isText) {
      return (
        <div style={styles.textViewer}>
          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
            Télécharger le fichier texte
          </a>
        </div>
      );
    }

    return (
      <div style={styles.unsupported}>
        <svg style={{ width: '4rem', height: '4rem', marginBottom: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <div style={{ fontSize: '1rem', fontWeight: 500, marginBottom: '0.5rem' }}>
          Aperçu non disponible
        </div>
        <div style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '1rem' }}>
          Ce type de fichier ne peut pas être affiché directement
        </div>
        <button onClick={handleDownload} style={styles.downloadButton}>
          <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Télécharger le fichier
        </button>
      </div>
    );
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.container} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            {document?.originalName || document?.name || 'Document'}
          </h3>
          <button
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.closeButtonHover.backgroundColor;
              e.currentTarget.style.color = styles.closeButtonHover.color;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = styles.closeButton.color;
            }}
          >
            ×
          </button>
        </div>
        <div style={styles.content}>
          {renderContent()}
        </div>
        {(document?.fileSize || document?.createdAt) && (
          <div style={styles.metadata}>
            <div style={{ display: 'flex', gap: '2rem', justifyContent: 'center' }}>
              {document.fileSize && (
                <span>Taille: {formatFileSize(document.fileSize)}</span>
              )}
              {document.createdAt && (
                <span>Ajouté le: {new Date(document.createdAt).toLocaleString()}</span>
              )}
              {document.fileType && (
                <span>Type: {document.fileType.split('/')[1]?.toUpperCase() || document.fileType}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;